import { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import {
  CasBooleanIcon,
  CasDataGridPro,
  CasMRTable,
  CasMRTableExportOptionMenu,
  CasMRTCellRenderLink,
  CasSwitch,
  ICasMRTCellProps,
} from 'src/compass/shared/components';
import CasLink from 'src/compass/shared/components/CasLink/CasLink';
import useRetrieve from 'src/compass/shared/hooks/reactQuery/useRetrieve';
import defaultLabelFromField from 'src/compass/shared/util/defaultLabelFromField';

import ClaimDetailsDialog from '../shared/ClaimDetails/ClaimDetailsDialog';

const HEADER_OVERRIDES: Record<string, string> = {
  policyNumber: 'Policy#',
  totalNumberInsured: 'HC',
  targetCWT: 'Target CWT',
  costPerHead: 'Cost/Head',
  costPerCWT: 'Cost/CWT',
  basePremium: 'Total Premium',
  netPremium: 'Producer Premium',
  hasClaim: 'Has Claim?',
  indemnityPerCWT: 'Indemnity/CWT',
  netIndemnityPerCWT: 'Net Indemnity/CWT',
  claimDueByDate: 'Claim Due By',
  noticeSentUser: 'Notice By',
  noticeSentDate: 'Notice Sent',
  claimReceivedDate: 'Received',
  claimReceivedUser: 'Rcvd By',
  claimAttachedDate: 'Attached',
  claimAttachedUser: 'Attached By',
  claimRejectedDate: 'Rejected',
  claimReviewedDate: 'Reviewed',
  claimNumber: 'Claim#',
  isFwdContract: 'Is Fwd Contract?',
};

const EXCLUDED_KEYS = ['id', 'subRows'];

const NARROW_COLUMNS = new Set(['sce', 'length', 'totalNumberInsured', 'targetCWT']);

function buildGridColumns(
  data: Record<string, any>[],
  onClickInsuredName: (id: any) => void
): GridColDef[] {
  if (!data.length) return [];

  return Object.keys(data[0])
    .filter((key) => !EXCLUDED_KEYS.includes(key))
    .map((key): GridColDef => {
      const isNarrow = NARROW_COLUMNS.has(key);
      const col: GridColDef = {
        field: key,
        headerName: HEADER_OVERRIDES[key] ?? defaultLabelFromField(key),
        flex: 1,
        minWidth: isNarrow ? 60 : 120,
        ...(isNarrow && { width: 60 }),
      };

      if (key === 'insuredName') {
        col.renderCell = (params: GridRenderCellParams) => (
          <CasLink onClick={() => onClickInsuredName(params.row.id)}>
            {params.value}
          </CasLink>
        );
      }

      if (key === 'hasClaim' || key === 'isFwdContract') {
        col.type = 'boolean';
        col.renderCell = (params: GridRenderCellParams) => {
          if (params.value == null) {
            return null; // no icon
          }

          return <CasBooleanIcon value={params.value}  falseIcon />;
          
        };
      }

      return col;
    });
}

export default function DataTab() {
  const queryResult = useRetrieve({ url: 'Sce/Claims/Data' });
  const [dialogRowId, setDialogRowId] = useState<any>(null);
  const [useDataGrid, setUseDataGrid] = useState(true);

  const rows = useMemo<Record<string, any>[]>(
    () => queryResult.data?.result ?? [],
    [queryResult.data?.result]
  );

  const gridColumns = useMemo(
    () => buildGridColumns(rows, (id) => setDialogRowId(id)),
    [rows]
  );

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        {CasMRTableExportOptionMenu('Sce/Claims/Data')}
        <CasSwitch
          label="Data Grid Pro"
          checked={useDataGrid}
          onChange={(_e, checked) => setUseDataGrid(checked)}
        />
      </Box>

      {useDataGrid ? (
        <CasDataGridPro
          rows={rows}
          columns={gridColumns}
          loading={queryResult.isLoading}
          autosizeOnMount
          initialState={{
            pinnedColumns: { left: ['insuredName', 'policyNumber', 'sce'] },
          }}
          density="compact"
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          sx={{ minHeight: 400 }}
        />
      ) : (
        <CasMRTable
          queryResult={queryResult}
          createColumnsFromData
          initialState={{ pagination: { pageIndex: 0, pageSize: 100 } }}
          overrideColumns={{
            insuredName: {
              Cell: (props: ICasMRTCellProps) => (
                <CasMRTCellRenderLink {...props} onClick={(row) => setDialogRowId(row.id)} />
              ),
            },
            policyNumber: { header: 'Policy#' },
            totalNumberInsured: { header: 'HC' },
            targetCWT: { header: 'Target CWT' },
            costPerHead: { header: 'Cost/Head' },
            costPerCWT: { header: 'Cost/CWT' },
            basePremium: { header: 'Total Premium' },
            netPremium: { header: 'Producer Premium' },
            hasClaim: { header: 'Has Claim?' },
            indemnityPerCWT: { header: 'Indemnity/CWT' },
            netIndemnityPerCWT: { header: 'Net Indemnity/CWT' },
            claimDueByDate: { header: 'Claim Due By' },
            noticeSentUser: { header: 'Notice By' },
            noticeSentDate: { header: 'Notice Sent' },
            claimReceivedDate: { header: 'Received' },
            claimReceivedUser: { header: 'Rcvd By' },
            claimAttachedDate: { header: 'Attached' },
            claimAttachedUser: { header: 'Attached By' },
            claimRejectedDate: { header: 'Rejected' },
            claimReviewedDate: { header: 'Reviewed' },
            claimNumber: { header: 'Claim#' },
            isFwdContract: { header: 'Is Fwd Contract?' },
          }}
          renderTopToolbarCustomActions={() => CasMRTableExportOptionMenu('Sce/Claims/Data')}
        />
      )}

      {dialogRowId && (
        <ClaimDetailsDialog
          id={dialogRowId}
          onClose={() => {
            setDialogRowId(null);
            queryResult.refetch();
          }}
        />
      )}
    </>
  );
}
